import React from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';

import { Row, Col, Divider } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';

import { BUSINESS_UNIT_TYPES } from '../../../../../../utils/constants/business-unit-types';
import { OrderProduct } from '../../../../../../api-client';
import { IShoppingFacilityGroup } from '../../../../../models/Shopping';
import { initWideFormOrderLine, initWideFormProduct, toggleWideFormDrawer } from '../../../../../../slices/WideForm/wideFormSlice';
import { orderTotalByProducts, orderQuantityBySizes, orderTotalBySizes } from '../../../../../../utils/data-format-enchanters/order-products-enchanter';
import { formatCurrency } from '../../../../../../utils/data-format-enchanters/currency-enchanter';
import { keepProduct } from '../../../../../../slices/OrderSummary/orderSummarySlice';

import './bv-summary-facility-group.scss';
import BVInputNumber from '../../../../BVControls/BVInputNumber/bv-input-number';

const BVSummaryFacilityGroup: React.FunctionComponent<IShoppingFacilityGroup> = ({ address, city, disabled, name, products }) => {
    const dispatch = useDispatch();

    const _handleFacilityGroupClick = (orderProduct: OrderProduct) => {
        if (disabled) {
            return;
        }

        if (orderProduct.id && orderProduct.rowId) {
            dispatch(initWideFormOrderLine(orderProduct.rowId));
            dispatch(initWideFormProduct(orderProduct.id));
            dispatch(toggleWideFormDrawer(true));
        }
    }

    const _handleSizeChange = (dataItem: OrderProduct, value: number) => {
        let editedSizes = _.map(dataItem.sizes, entryItem => (
            entryItem.sapCode === dataItem.sizes![0].sapCode
                ? { ...entryItem, quantity: value }
                : entryItem
        ));

        dispatch(keepProduct({ ...dataItem, sizes: editedSizes }));
    };

    return (
        <div className="bv-summary-facility-group-container">
            <Row>
                <Col span={12}>
                    <span className="heading">
                        {name}
                    </span>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                    <span className="heading">
                        {orderTotalByProducts(products).amountFormat}
                    </span>
                </Col>
                <Col span={24}>
                    <span className="secondary-heading">
                        {`${address}, ${city}`}
                    </span>
                </Col>
                <Col span={24}>
                    <table className="facility-group-table">
                        <tbody>
                            {_.map(products, (dataItem, dataIndex) => (
                                <tr
                                    key={dataIndex}
                                    className={`facility-group-table-row ${disabled && 'facility-group-table-row-disabled'}`}
                                    onClick={() => _handleFacilityGroupClick(dataItem)}
                                >
                                    <td style={{ width: '15%' }}>
                                        <img src={dataItem.imageUrl} className="group-table-image" alt={dataItem.name} />
                                    </td>
                                    <td style={{ width: '35%' }}>
                                        <span className="group-table-text">
                                            {dataItem.name}
                                        </span>
                                    </td>
                                    <td style={{ width: '10%', textAlign: 'center' }}>
                                        <span className="group-table-text-small">
                                            {dataItem.businessUnitCode === BUSINESS_UNIT_TYPES.JEWELRY_CODE
                                                ? `CatCode`
                                                : `SapCode`
                                            }
                                        </span>
                                        <span className="group-table-text">
                                            {dataItem.businessUnitCode === BUSINESS_UNIT_TYPES.JEWELRY_CODE
                                                ? `${dataItem.catalogCode}`
                                                : `${dataItem.sizes && dataItem.sizes.length > 0 && dataItem.sizes[0].sapCode}`
                                            }
                                        </span>
                                    </td>
                                    <td style={{ width: '10%', textAlign: 'center' }}>
                                        <span className="group-table-text" onClick={(e: any) => {
                                            if (dataItem.sizes?.length === 1) {
                                                e.stopPropagation();
                                                e.preventDefault();
                                            } else {
                                                return;
                                            }
                                        }}>
                                            {dataItem.sizes?.length === 1
                                                ?
                                                <BVInputNumber
                                                    className={dataItem.sizes && orderQuantityBySizes(dataItem.sizes) > 1 ? 'bv-size-control' : ''}
                                                    properties={{
                                                        value: orderQuantityBySizes(dataItem.sizes) || 0,
                                                        step: 1,
                                                        upHandler: <PlusOutlined />,
                                                        downHandler: <MinusOutlined />,
                                                        min: 1,
                                                        onChange: (value: number) => {
                                                            _handleSizeChange(dataItem, value);
                                                        },
                                                        onStep: (value: number, info: { offset: number, type: 'up' | 'down' }) => {
                                                            _handleSizeChange(dataItem, value);
                                                        },
                                                    }}
                                                />
                                                :
                                                `x ${orderQuantityBySizes(dataItem.sizes)}`
                                            }
                                        </span>
                                    </td>
                                    <td style={{ width: '15%', textAlign: 'center' }}>
                                        <span className="group-table-text-small">
                                            Unit Price
                                        </span>
                                        <span className="group-table-text">
                                            {dataItem.sizes && dataItem.sizes[0].priceNet &&
                                                `${formatCurrency(dataItem.sizes[0].priceNet.amount, dataItem.sizes[0].priceNet.currency)}`
                                            }
                                        </span>
                                    </td>
                                    <td style={{ width: '15%', textAlign: 'center' }}>
                                        <span className="group-table-text-small">
                                            Amount
                                        </span>
                                        <span className="group-table-text">
                                            {`${orderTotalBySizes(dataItem.sizes).amountFormat}`}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Col>
            </Row>

            <Divider />
        </div>
    );
}

export default BVSummaryFacilityGroup;